import { Consumer, Subscription } from '@rails/actioncable';
import { JobLoadActivity_Broadcast, Load_Read } from '@treadinc/horizon-api-spec';
import { Dispatch } from 'react';

import connection from '~services/connectionModule';

export const RTU_WAITING_TIMEOUT_IN_MS = 60 * 1000;

/**
 * Narrows a JobLoadActivityChannel broadcast's data: create/update actions carry a full
 * Load_Read while delete carries only an Identifier.
 */
export const jobLoadActivityDataIsLoadRead = (
  data: JobLoadActivity_Broadcast['data'],
): data is Load_Read => {
  return 'job_id' in data;
};

/**
 * Narrows a channel message to its payload. A channel that refuses a subscription transmits
 * `{ error, message }` before rejecting it, and that envelope reaches `received` like a normal
 * broadcast but carries no `data`. It is absent from the OpenAPI spec, so it is narrowed by hand.
 */
export const channelMessageData = <T>(message: unknown): T | undefined => {
  if (!message || typeof message !== 'object' || !('data' in message)) {
    console.warn('[RTU] Ignoring a channel message with no data payload:', message);

    return undefined;
  }

  return (message as { data: T }).data ?? undefined;
};

/**
 * Subscribes to a channel using ActionCable and creates a new subscription if one doesn't exist.
 * subscription state will be set to the new subscription or the existing one.
 *
 * @param channel - The name of the channel to subscribe to.
 * @param companyId - The company ID to use for the subscription.
 * @param receivedHandler - The callback function to handle received data from the channel.
 * @param createOptions - The options to create the subscription with. Only applicable for certain channels.
 */
export const subscribeToChannel = <T>(
  channel: string,
  companyId: string | null,
  setSubscription: Dispatch<Subscription<Consumer>>, //(subscription: unknown) => void,
  receivedHandler: (resp: T) => void,
  createOptions?: Record<string, unknown>,
) => {
  const consumer = connection.realTimeConnection;

  let subscription = consumer?.subscriptions.subscriptions.find((sub) =>
    sub.identifier.includes(channel),
  );

  // If there is no existing subscription, create a new one
  if (!subscription) {
    subscription = consumer?.subscriptions?.create?.(
      {
        channel,
        ...(companyId ? { company_id: companyId } : {}),
        ...createOptions,
      },
      {
        connected: () => () => {},
        disconnected: () => () => {},
        received: receivedHandler,
      },
    );
  }

  setSubscription(subscription);
};
